import { Lock, Sparkles, Zap } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useModule } from '@/hooks/useOrganizationModules'
import { useSubscription } from '@/hooks/useSubscription'
import { useNavigate } from 'react-router-dom'
import { PLANS } from '@/lib/stripe'

interface UpgradePromptProps {
  moduleId: string
  message?: string
  compact?: boolean
}

/**
 * Component that shows an upgrade prompt when a feature is locked.
 * Shows tier-based pricing instead of per-module pricing.
 */
export function UpgradePrompt({ moduleId, message, compact = false }: UpgradePromptProps) {
  const module = useModule(moduleId)
  const navigate = useNavigate()
  const { isTrial, isTrialExpired } = useSubscription()

  if (!module) {
    return null
  }

  const handleUpgrade = () => {
    navigate('/pricing')
  }

  // Determine which tier is needed for this module
  const requiredTier = module.category === 'enterprise' ? 'enterprise' : 'professional'
  const requiredPlan = PLANS[requiredTier]

  const icon =
    module.category === 'professional' ? (
      <Zap className="h-5 w-5 text-blue-500" />
    ) : module.category === 'enterprise' ? (
      <Sparkles className="h-5 w-5 text-purple-500" />
    ) : (
      <Lock className="h-5 w-5 text-muted-foreground" />
    )

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
        {icon}
        <span className="text-sm text-muted-foreground flex-1">
          {message || `${module.name} requires ${requiredPlan.name} plan`}
        </span>
        <Button size="sm" variant="default" onClick={handleUpgrade}>
          {isTrialExpired ? 'Subscribe' : 'Upgrade'}
        </Button>
      </div>
    )
  }

  const categoryColor =
    module.category === 'professional'
      ? 'bg-blue-100 text-blue-800'
      : module.category === 'enterprise'
        ? 'bg-purple-100 text-purple-800'
        : 'bg-gray-100 text-gray-800'

  return (
    <Card className="border-2 border-dashed">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {icon}
              <CardTitle className="text-lg">{module.name}</CardTitle>
            </div>
            <CardDescription>{message || module.description}</CardDescription>
          </div>
          <Badge className={categoryColor}>{requiredPlan.name}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isTrialExpired && isTrial ? (
            <p className="text-sm text-muted-foreground">
              Your free trial has ended. Subscribe to the {requiredPlan.name} plan to access this feature.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              This feature is included in the {requiredPlan.name} plan.
            </p>
          )}

          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold">
              £{requiredPlan.monthlyPrice}
            </span>
            <span className="text-muted-foreground">/month</span>
            <span className="text-sm text-muted-foreground ml-2">
              or £{requiredPlan.annualPrice}/year
            </span>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleUpgrade} className="flex-1">
              {isTrialExpired ? 'Subscribe Now' : `Upgrade to ${requiredPlan.name}`}
            </Button>
            <Button variant="outline" onClick={() => navigate('/pricing')}>
              Compare Plans
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
